import { PaymentDto } from 'api/billing';
import { Calendar, CreditCard, Receipt, FileText, CheckCircle, Clock } from 'lucide-react';

interface PaymentScheduleDetailsProps {
  payment: PaymentDto;
}

const formatDate = (date?: Date | string): string => {
  if (!date) return '-';
  return new Date(date).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR'
  }).format(amount);
};

export function PaymentScheduleDetails({ payment }: PaymentScheduleDetailsProps) {
  return (
    <div className="space-y-6">
      {/* Header avec statut */}
      <div className="flex items-center justify-between pb-4 border-b">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-full ${payment.isPaid ? 'bg-green-100' : 'bg-orange-100'}`}>
            {payment.isPaid ? (
              <CheckCircle className="w-5 h-5 text-green-600" />
            ) : (
              <Clock className="w-5 h-5 text-orange-600" />
            )}
          </div>
          <div>
            <p className="text-sm text-gray-500">Échéance n°{payment.paymentNumber}</p>
            <p className={`font-medium ${payment.isPaid ? 'text-green-600' : 'text-orange-600'}`}>
              {payment.status}
            </p>
          </div>
        </div>
        <div className="text-right">
          <p className="text-sm text-gray-500">Montant total</p>
          <p className="text-2xl font-semibold text-gray-900">
            {formatCurrency(payment.clientPrice)}
          </p>
        </div>
      </div>

      {/* Dates */}
      <div className="grid grid-cols-3 gap-4">
        <div className="p-4 bg-gray-50 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <Calendar className="w-4 h-4 text-gray-400" />
            <span className="text-sm text-gray-500">Date prévue</span>
          </div>
          <p className="font-medium text-gray-900">{formatDate(payment.scheduledDate)}</p>
        </div>
        <div className="p-4 bg-gray-50 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <Calendar className="w-4 h-4 text-gray-400" />
            <span className="text-sm text-gray-500">Date d'échéance</span>
          </div>
          <p className="font-medium text-gray-900">{formatDate(payment.dueDate)}</p>
        </div>
        <div className="p-4 bg-gray-50 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <CreditCard className="w-4 h-4 text-gray-400" />
            <span className="text-sm text-gray-500">Date de prélèvement</span>
          </div>
          <p className="font-medium text-gray-900">{formatDate(payment.withdrawalDate)}</p>
        </div>
      </div>

      {/* Référence prélèvement */}
      {payment.directDebitReference && (
        <div className="p-4 bg-blue-50 rounded-lg">
          <div className="flex items-center gap-2">
            <FileText className="w-4 h-4 text-blue-500" />
            <span className="text-sm text-blue-700">Référence de prélèvement</span>
          </div>
          <p className="mt-1 font-mono text-sm text-blue-900">{payment.directDebitReference}</p>
        </div>
      )}

      {/* Décomposition du montant */}
      <div className="border rounded-lg overflow-hidden">
        <div className="px-4 py-3 bg-gray-50 border-b">
          <div className="flex items-center gap-2">
            <Receipt className="w-4 h-4 text-gray-500" />
            <span className="font-medium text-gray-700">Décomposition du montant</span>
          </div>
        </div>
        <div className="divide-y">
          <div className="px-4 py-3 flex justify-between">
            <span className="text-gray-600">Montant HT</span>
            <span className="font-medium text-gray-900">{formatCurrency(payment.priceExcludingTax)}</span>
          </div>
          <div className="px-4 py-3 flex justify-between">
            <span className="text-gray-600">Taxes</span>
            <span className="font-medium text-gray-900">{formatCurrency(payment.tax)}</span>
          </div>
          {payment.feesAmount > 0 && (
            <div className="px-4 py-3 flex justify-between">
              <span className="text-gray-600">Frais</span>
              <span className="font-medium text-gray-900">{formatCurrency(payment.feesAmount)}</span>
            </div>
          )}
          <div className="px-4 py-3 flex justify-between bg-gray-50">
            <span className="font-medium text-gray-700">Total TTC</span>
            <span className="font-semibold text-gray-900">{formatCurrency(payment.clientPrice)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
